import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from 'src/environments/environment';
import { endpoints } from 'src/environments/endpoints';
import {
  LoginRequest,
  LoginResponse,
  SignInCredentials,
  TwoFactorValidationRequest,
  TwoFactorValidationResponse,
} from '../../models/auth.model';
import { EncryptionService } from '../encryption/encryption.service';

/**
 * Token de "parceiro" herdado do backend antigo (POST /api/Auth/Credentials).
 * A API v1 não exige este header — mantido temporariamente por compatibilidade.
 * TODO: remover após confirmar com o backend que não é mais necessário.
 */
const PARTNER_AUTH_TOKEN =
  'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJwcmltYXJ5c2lkIjoiMiIsImh0dHA6Ly9zY2hlbWFzLnhtbHNvYXAub3JnL3dzLzIwMDUvMDUvaWRlbnRpdHkvY2xhaW1zL2xvY2FsaXR5IjoiRGV2ZWxvcG1lbnQiLCJjaGF2ZSI6ImUzYjBjNDQyOThmYzFjMTQ5YWZiZjRjODk5NmZiOTI0MjdhZTQxZTQ2NDliOTM0Y2E0OTU5OTFiNzg1MmI4NTUiLCJuYmYiOjE3MzY0NTU5MTgsImV4cCI6MjA1MTk4ODcxOCwiaWF0IjoxNzM2NDU1OTE4fQ.Usg3p59_ZR0HOtp7fn5i4C7LIE5qzb3T0TPr4E82c38';

interface SignInPayload {
  usuarioLogin: string;
  senha: string;
  recaptcha: string;
}

interface TwoFactorPayload {
  user: string;
  userCode?: string;
  twoFACode: string;
}

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private readonly baseUrl = environment.apiUrls.infraestrutura;

  private readonly partnerHeaders = new HttpHeaders({
    'Content-Type': 'application/json',
    Authorization: `Bearer ${PARTNER_AUTH_TOKEN}`,
  });

  constructor(
    private http: HttpClient,
    private encryption: EncryptionService
  ) {}

  /**
   * POST /api/v1/auth/login
   * Credenciais em texto puro (a API v1 não usa RSA como o fluxo antigo).
   * Retorna token de sessão + usuário completo (menus, tenants, grupo).
   */
  signIn(credentials: SignInCredentials): Observable<LoginResponse> {
    const payload: SignInPayload = {
      usuarioLogin: credentials.usuarioLogin,
      senha: credentials.senha,
      recaptcha: credentials.recaptchaToken,
    };

    return this.http.post<LoginResponse>(
      `${this.baseUrl}/${endpoints.infraestrutura.loginV1}`,
      payload,
      {
        headers: this.partnerHeaders,
      }
    );
  }

  /**
   * LEGADO — fluxo de 2FA do backend antigo (Auth/2FA/Validate).
   * Não é mais chamado; mantido até a implementação do 2FA na API v1.
   * TODO: remover ou consolidar com validateTwoFactorV1.
   */
  validateTwoFactor(
    request: TwoFactorValidationRequest
  ): Observable<TwoFactorValidationResponse> {
    const payload: TwoFactorPayload = {
      user: request.encryptedUser,
      userCode: request.userCode,
      twoFACode: this.encryption.encrypt(request.codigo),
    };

    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      Authorization: `Bearer ${request.intermediateToken}`,
    });

    return this.http.post<TwoFactorValidationResponse>(
      `${this.baseUrl}/${endpoints.infraestrutura.validateTwoFactor}`,
      payload,
      { headers }
    );
  }

  /**
   * TODO: método não utilizado — signIn() já consome o endpoint v1.
   * Manter apenas se o contrato de LoginRequest divergir no futuro.
   */
  loginV1(request: LoginRequest): Observable<LoginResponse> {
    return this.http.post<LoginResponse>(
      `${this.baseUrl}/${endpoints.infraestrutura.loginV1}`,
      request
    );
  }

  /**
   * TODO: método não utilizado — aguardando definição do fluxo de 2FA na v1.
   * O payload abaixo replica o formato antigo e precisa ser validado.
   */
  validateTwoFactorV1(
    request: TwoFactorValidationRequest
  ): Observable<LoginResponse> {
    const payload = {
      user: request.encryptedUser,
      userCode: request.userCode,
      twoFACode: this.encryption.encrypt(request.codigo),
    };

    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      Authorization: `Bearer ${request.intermediateToken}`,
    });

    return this.http.post<LoginResponse>(
      `${this.baseUrl}/${endpoints.infraestrutura.validateTwoFactorV1}`,
      payload,
      { headers }
    );
  }
}